'use client';
import React, { useState } from 'react';
import AppImage from '@/components/ui/AppImage';
import ConfirmModal from '@/components/ui/ConfirmModal';
import { CheckCircle, XCircle, RotateCcw, Cpu } from 'lucide-react';
import { toast } from 'sonner';

const INITIAL_QUEUE = [
{ id: 'ai-001', type: 'Image', entity: 'Buddy — Labrador', content: 'Profile photo uploaded 2h ago', aiReason: 'Possible nudity detected', confidence: 94, category: 'Content Safety', photo: "https://img.rocket.new/generatedImages/rocket_gen_img_1c30f8282-1769025202966.png", photoAlt: 'AI flagged image from Buddy profile', autoAction: 'Hold for review' },
{ id: 'ai-002', type: 'Profile', entity: 'SpamDog123 — Unknown', content: 'New profile created 1h ago', aiReason: 'Bot-like signup pattern', confidence: 89, category: 'Spam / Bot', photo: "https://img.rocket.new/generatedImages/rocket_gen_img_107853d1c-1766924923256.png", photoAlt: 'AI flagged profile photo', autoAction: 'Hold for review' },
{ id: 'ai-003', type: 'Guestbook', entity: 'Luna — Samoyed', content: '"Check out my shop for dog products..."', aiReason: 'Promotional spam detected', confidence: 97, category: 'Spam / Promotional', photo: null, photoAlt: '', autoAction: 'Auto-removed' },
{ id: 'ai-004', type: 'Image', entity: 'Max — Rottweiler', content: 'Memory photo uploaded 3h ago', aiReason: 'Human face visible in photo', confidence: 76, category: 'Privacy', photo: "https://img.rocket.new/generatedImages/rocket_gen_img_106d64c7f-1772814240355.png", photoAlt: 'AI flagged image showing human faces', autoAction: 'Hold for review' },
{ id: 'ai-005', type: 'Profile', entity: 'FluffyPaws99 — Poodle', content: 'Profile bio updated 30min ago', aiReason: 'Suspicious external links in bio', confidence: 81, category: 'Spam / Links', photo: "https://img.rocket.new/generatedImages/rocket_gen_img_1e15391f0-1772227288194.png", photoAlt: 'AI flagged profile photo for FluffyPaws99', autoAction: 'Hold for review' },
{ id: 'ai-006', type: 'Guestbook', entity: 'Archie — Dachshund', content: '"This dog is the worst dog I\'ve ever..."', aiReason: 'Hate speech / harassment detected', confidence: 88, category: 'Harassment', photo: null, photoAlt: '', autoAction: 'Auto-removed' },
{ id: 'ai-007', type: 'Image', entity: 'Bella — Golden Retriever', content: 'Portrait Studio output', aiReason: 'Unrealistic / deepfake image', confidence: 62, category: 'Authenticity', photo: "https://img.rocket.new/generatedImages/rocket_gen_img_107853d1c-1766924923256.png", photoAlt: 'AI flagged portrait studio output', autoAction: 'Hold for review' }];


const TYPE_STYLES: Record<string, string> = {
  Image: 'bg-blue-50 text-info border-blue-200',
  Profile: 'bg-violet-50 text-primary border-violet-200',
  Guestbook: 'bg-amber-50 text-accent border-amber-200'
};

const ACTION_STYLES: Record<string, string> = {
  'Hold for review': 'bg-yellow-50 text-yellow-700 border-yellow-200',
  'Auto-removed': 'bg-red-50 text-danger border-red-200'
};

export default function AIQueueTable() {
  const [queue, setQueue] = useState(INITIAL_QUEUE);
  const [confirmAction, setConfirmAction] = useState<{id: string;action: string;} | null>(null);

  const handleAction = (id: string, action: string) => {
    setQueue((q) => q.filter((item) => item.id !== id));
    setConfirmAction(null);
    const labels: Record<string, string> = {
      approve: 'Item approved and cleared from queue.',
      remove: 'Item permanently removed.',
      restore: 'Item restored and flag dismissed.'
    };
    toast.success(labels[action] || 'Action completed.');
  };

  return (
    <>
      <div className="flex items-center gap-2 mb-4 p-3 bg-violet-50 border border-violet-200 rounded-xl">
        <Cpu size={15} className="text-primary shrink-0" />
        <p className="text-xs text-foreground">
          <span className="font-700">AI Moderation</span> automatically flags and holds suspicious content. Review and confirm each action below.
        </p>
      </div>

      <div className="bg-card border border-border rounded-2xl shadow-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border bg-muted/40">
                <th className="text-left px-5 py-3 text-xs font-600 text-muted-foreground">Type</th>
                <th className="text-left px-4 py-3 text-xs font-600 text-muted-foreground">Entity</th>
                <th className="text-left px-4 py-3 text-xs font-600 text-muted-foreground">Content</th>
                <th className="text-left px-4 py-3 text-xs font-600 text-muted-foreground">AI Reason</th>
                <th className="text-left px-4 py-3 text-xs font-600 text-muted-foreground">Category</th>
                <th className="text-center px-4 py-3 text-xs font-600 text-muted-foreground">Confidence</th>
                <th className="text-left px-4 py-3 text-xs font-600 text-muted-foreground">Auto Action</th>
                <th className="text-center px-4 py-3 text-xs font-600 text-muted-foreground">Review</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {queue.map((item) =>
              <tr key={item.id} className="hover:bg-muted/30 transition-colors">
                  <td className="px-5 py-4">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-600 border ${TYPE_STYLES[item.type]}`}>
                      {item.type}
                    </span>
                  </td>
                  <td className="px-4 py-4">
                    <div className="flex items-center gap-2">
                      {item.photo &&
                    <div className="w-8 h-8 rounded-lg overflow-hidden bg-muted shrink-0">
                          <AppImage src={item.photo} alt={item.photoAlt} width={32} height={32} className="object-cover w-full h-full" />
                        </div>
                    }
                      <p className="font-700 text-xs text-foreground whitespace-nowrap">{item.entity}</p>
                    </div>
                  </td>
                  <td className="px-4 py-4 max-w-[140px]">
                    <p className="text-xs text-foreground/80 line-clamp-2">{item.content}</p>
                  </td>
                  <td className="px-4 py-4 max-w-[160px]">
                    <p className="text-xs text-foreground line-clamp-2">{item.aiReason}</p>
                  </td>
                  <td className="px-4 py-4">
                    <span className="text-xs font-600 text-muted-foreground whitespace-nowrap">{item.category}</span>
                  </td>
                  <td className="px-4 py-4 text-center">
                    <div className="flex flex-col items-center gap-1">
                      <span className={`text-sm font-800 stat-badge ${item.confidence >= 80 ? 'text-danger' : item.confidence >= 60 ? 'text-warning' : 'text-muted-foreground'}`}>
                        {item.confidence}%
                      </span>
                      <div className="w-10 h-1 bg-muted rounded-full overflow-hidden">
                        <div
                        className={`h-full rounded-full ${item.confidence >= 80 ? 'bg-danger' : item.confidence >= 60 ? 'bg-warning' : 'bg-muted-foreground'}`}
                        style={{ width: `${item.confidence}%` }} />
                      
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-4">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-600 border ${ACTION_STYLES[item.autoAction]}`}>
                      {item.autoAction}
                    </span>
                  </td>
                  <td className="px-4 py-4">
                    <div className="flex items-center justify-center gap-1">
                      <button
                      onClick={() => setConfirmAction({ id: item.id, action: 'approve' })}
                      className="w-7 h-7 rounded-lg bg-green-50 flex items-center justify-center text-success hover:bg-green-100 transition-colors"
                      title="Approve — dismiss flag and restore content">
                      
                        <CheckCircle size={13} />
                      </button>
                      <button
                      onClick={() => setConfirmAction({ id: item.id, action: 'remove' })}
                      className="w-7 h-7 rounded-lg bg-red-50 flex items-center justify-center text-danger hover:bg-red-100 transition-colors"
                      title="Confirm removal — permanently delete this content">
                      
                        <XCircle size={13} />
                      </button>
                      <button
                      onClick={() => setConfirmAction({ id: item.id, action: 'restore' })}
                      className="w-7 h-7 rounded-lg bg-muted flex items-center justify-center text-muted-foreground hover:text-primary hover:bg-secondary transition-colors"
                      title="Restore — undo AI auto-remove action">
                      
                        <RotateCcw size={13} />
                      </button>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {queue.length === 0 &&
        <div className="flex flex-col items-center justify-center py-16 text-center">
            <Cpu size={40} className="text-success mb-3" />
            <p className="font-700 text-base text-foreground">AI queue is empty</p>
            <p className="text-sm text-muted-foreground mt-1">All AI-flagged items have been reviewed.</p>
          </div>
        }
      </div>

      <ConfirmModal
        open={!!confirmAction}
        title={
        confirmAction?.action === 'approve' ? 'Approve & Dismiss Flag' :
        confirmAction?.action === 'remove' ? 'Confirm Removal' : 'Restore Content'
        }
        description={
        confirmAction?.action === 'approve' ? 'This will dismiss the AI flag and restore the content as safe.' :
        confirmAction?.action === 'remove' ? 'This will permanently delete the flagged content. This cannot be undone.' :
        'This will restore the content that was auto-removed by AI and dismiss the flag.'
        }
        confirmLabel={
        confirmAction?.action === 'approve' ? 'Approve' :
        confirmAction?.action === 'remove' ? 'Confirm Removal' : 'Restore'
        }
        destructive={confirmAction?.action === 'remove'}
        onConfirm={() => confirmAction && handleAction(confirmAction.id, confirmAction.action)}
        onCancel={() => setConfirmAction(null)} />
      
    </>);

}